import { useContext, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiContext } from "../../context/ApiContext";
import { ModalContext } from "../../context/ModalContext";
import endpoints from "../../data/endpoints";
import getStepData from "../../requests/getStepData";
import Loader from "../../shared/Loader";
import { ApiRequestData, FormInputs } from "../../types/types";
import { findClubByLocation } from "../../utils/getClubSlugFromName";
import StepAdvisorLogin from "./stepAdvisorLogin";
import StepContact from "./stepContact";
import StepDetails from "./stepDetails";
import StepPayment from "./stepPayment";
import StepPricing from "./stepPricing";
import StepSummary from "./stepSummary";

export const onSubmit: SubmitHandler<FormInputs> = (data) => console.log(data);

const FormSteps = (): React.JSX.Element => {
	const methods = useForm<FormInputs>();
	// const { step } = useContext(StepContext);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { data, setData } = useContext(ApiContext);
	const { isModalOpen } = useContext(ModalContext);
	const [isLoading, setIsLoading] = useState(true);

	// Get URL params.
	const urlParams = Object.fromEntries(searchParams.entries());
	const { step, clubId, membershipTypeId, location } = urlParams;

	const redirectToContact = () => {
		navigate("?step=contact", { replace: true });
		setIsLoading(false);
	};

	useEffect(() => {
		if ("summary" === step && clubId && membershipTypeId) {
			getMembershipDetails();
		} else if ("pricing" === step && clubId) {
			getMembershipPlans();
		} else if ("pricing" === step && location) {
			getMembershipPlansByLocation();
		} else if ("pricing" === step) {
			redirectToContact();
		} else {
			setIsLoading(false);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Get membership details for direct links.
	const getMembershipDetails = async () => {
		const query: ApiRequestData = [
			{
				action: "get_membership_details",
				query: [
					{
						name: "clubID",
						value: Number(clubId),
					},
					{
						name: "membershipTypeId",
						value: Number(membershipTypeId),
					},
				],
			},
		];

		const response = await getStepData(0, endpoints.getMembershipInfo, query);

		setData({
			...data,
			get_club_id: clubId,
			selectedClub: response?.data?.MembershipDetails?.Clubs,
			selectedMembership: response?.data?.MembershipDetails?.Membership,
		});

		setIsLoading(false);
	};

	// Get membership plans for direct links.
	const getMembershipPlans = async () => {
		const query: ApiRequestData = [
			{
				action: "get_clubs",
				query: [],
			},
		];

		const response = await getStepData(0, endpoints.step0, query);

		const clubs = response?.data?.Clubs;

		setData({
			...data,
			get_club_id: clubId,
			selectedClub: clubs.find(
				(club: any) => Number(club.ClubID) === Number(clubId),
			),
		});

		setIsLoading(false);
	};

	const getMembershipPlansByLocation = async () => {
		const query: ApiRequestData = [
			{
				action: "get_clubs",
				query: [],
			},
		];

		const response = await getStepData(0, endpoints.step0, query);
		const clubs = response?.data?.Clubs;
		const selectedClub = findClubByLocation(clubs, location);

		if (!selectedClub || selectedClub.RequireCheckPrice !== false) {
			redirectToContact();
			return;
		}

		setData({
			...data,
			get_club_id: selectedClub.ClubID,
			selectedClub,
		});

		setIsLoading(false);
	};

	if (isLoading) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<Loader colorCircle="#000" colorRotator="#ddd" />
			</div>
		);
	}

	const renderCurrentStep = (step: String) => {
		switch (step) {
			case "advisor-login":
				return <StepAdvisorLogin />;
			case "contact":
				return <StepContact />;
			case "pricing":
				return <StepPricing />;
			case "summary":
				return <StepSummary />;
			case "details":
				return <StepDetails />;
			case "payment":
				return <StepPayment />;
			default:
				return <StepContact />;
		}
	};

	const classNames = `${
		isModalOpen && "overflow-y-hidden"
	} flex flex-col justify-center`;

	return (
		<FormProvider {...methods}>
			<div className={classNames}>{renderCurrentStep(step)}</div>
		</FormProvider>
	);
};

export default FormSteps;
